import { useState } from "react";
import { CreditCard } from "lucide-react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { ItemList } from "@/shared/components/ui/ItemList";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { usePaymentMethods, useRemovePaymentMethod } from "../hooks/paymentMethodQueries";
import type { PaymentMethod } from "../types/paymentMethodTypes";

function PaymentMethods({ businessId, clientId, userId }: { businessId: string; clientId: string; userId: string }) {
  const query = usePaymentMethods(businessId, clientId, userId);
  const removal = useRemovePaymentMethod(businessId, clientId, userId);
  const { goToClientPaymentMethodAdd } = useAppNavigation();
  const capture = useWorkspaceOperation();
  const [selected, setSelected] = useState<PaymentMethod | null>(null);
  return <Section title="Payment methods">
    <p className="text-sm text-muted-foreground">Cards are stored securely by Stripe for this business. To collect an agreed payment, open the customer's appointment and choose Charge.</p>
    {query.isPending ? <RowSkeleton /> : query.error ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> : <>
      <p className="text-xs text-muted-foreground">{query.data.environment === "test" ? "Test mode - use test cards only." : "Live mode"}</p>
      <ItemList items={query.data.cards} emptyState={{ message: "No saved cards." }} renderItem={card =>
        <InfoRow title={`${card.brand} ending in ${card.last4}`}
          subtitle={`Expires ${String(card.expMonth).padStart(2, "0")}/${card.expYear}`}
          icon={<CreditCard aria-hidden="true" />} actions={
            <Button variant="quiet" disabled={removal.isPending} aria-label={`Remove card ending in ${card.last4}`}
              onClick={() => { removal.reset(); setSelected(card); }}>Remove</Button>
          } />} />
      <Button variant="outline" onClick={() => goToClientPaymentMethodAdd(clientId)}>Add payment method</Button>
    </>}
    <Button variant="quiet" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh cards</Button>
    <ConfirmationDialog open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}
      title="Remove saved card?" description={`Remove the card ending in ${selected?.last4 ?? ""} from this customer's Stripe account. This does not refund or charge anything.`}
      confirmLabel="Remove card" dismissLabel="Keep card" destructive pending={removal.isPending} error={removal.error?.message}
      onConfirm={() => {
        if (!selected || removal.isPending) return;
        const isCurrent = capture();
        removal.mutate(selected.id, { onSuccess: () => { if (isCurrent()) setSelected(null); } });
      }} />
  </Section>;
}
export function CustomerPaymentMethods({ clientId }: { clientId: string }) {
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  if (!business || !businessId || !user || business.ownerId !== user.id) return null;
  return <PaymentMethods key={`${user.id}:${businessId}:${clientId}`} businessId={businessId} clientId={clientId} userId={user.id} />;
}
